import { useState } from "react";

import { initialCandidateProfile } from "@/modules/candidate/constants/candidateApplication.constants";

import type {
  CandidateApplicationData,
  ResumeParsedData,
} from "@/modules/candidate/types/candidateApplication.types";

type ProfileField =
  | "fullName"
  | "email"
  | "phone"
  | "currentLocation"
  | "currentCompany"
  | "currentDesignation"
  | "totalExperience"
  | "highestQualification"
  | "currentSalary"
  | "expectedSalary"
  | "noticePeriod"
  | "linkedinUrl"
  | "portfolioUrl";

const getCleanString = (
  value: unknown
): string => {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
};

const normalizePhoneNumber = (value: unknown): string => {
  if (!value) {
    return "";
  }

  let phone = String(value).trim();

  // Keep only digits and +
  phone = phone.replace(/[^\d+]/g, "");

  // Remove India country code
  if (phone.startsWith("+91")) {
    phone = phone.slice(3);
  } else if (phone.startsWith("91") && phone.length === 12) {
    phone = phone.slice(2);
  }

  // Keep only digits
  phone = phone.replace(/\D/g, "");

  // Indian mobile number = last 10 digits
  if (phone.length > 10) {
    phone = phone.slice(-10);
  }

  return phone;
};


const getFirstNonEmpty = (
  ...values: unknown[]
): string => {
  for (const value of values) {
    const cleanValue =
      getCleanString(value);

    if (cleanValue) {
      return cleanValue;
    }
  }

  return "";
};


const uniqueStrings = (
  values: unknown[]
): string[] => {
  const result: string[] = [];
  const seen = new Set<string>();

  values.forEach((value) => {
    const cleanValue =
      getCleanString(value);

    if (!cleanValue) {
      return;
    }

    const key =
      cleanValue.toLowerCase();

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    result.push(cleanValue);
  });

  return result;
};


const buildSkillsValue = (
  parsedData: ResumeParsedData
): string => {
  const skills =
    parsedData.skills;

  if (!skills) {
    return "";
  }

  return uniqueStrings([
    ...(skills.technical || []),
    ...(skills.frameworks || []),
    ...(skills.tools || []),
    ...(skills.databases || []),
    ...(skills.cloud || []),
    ...(skills.soft || []),
    ...(skills.languages || []),
  ]).join(", ");
};


const getCurrentOrLatestExperience = (
  parsedData: ResumeParsedData
) => {
  const experience =
    parsedData.experience || [];

  if (!experience.length) {
    return null;
  }

  const currentExperience =
    experience.find(
      (item) =>
        item.currentlyWorking === true
    );

  if (currentExperience) {
    return currentExperience;
  }

  /*
   * Resume parser usually returns experience
   * from latest to oldest. Use the first one
   * when currentlyWorking is not available.
   */
  return experience[0];
};


const getHighestQualification = (
  parsedData: ResumeParsedData
): string => {
  const education =
    parsedData.education || [];

  if (!education.length) {
    return "";
  }

  const educationRank = (
    educationLevel: string
  ): number => {
    const value =
      educationLevel
        .toLowerCase()
        .trim();

    if (
      value.includes("phd") ||
      value.includes("doctor") ||
      value.includes("doctoral")
    ) {
      return 6;
    }

    if (
      value.includes("master") ||
      value.includes("postgraduate") ||
      value.includes("post graduate") ||
      value.includes("mba") ||
      value.includes("m.tech") ||
      value.includes("mtech") ||
      value.includes("mca")
    ) {
      return 5;
    }

    if (
      value.includes("bachelor") ||
      value.includes("undergraduate") ||
      value.includes("b.tech") ||
      value.includes("btech") ||
      value.includes("bca") ||
      value.includes("be")
    ) {
      return 4;
    }

    if (
      value.includes("diploma") ||
      value.includes("polytechnic")
    ) {
      return 3;
    }

    if (
      value.includes("12th") ||
      value.includes("higher secondary") ||
      value.includes("senior secondary")
    ) {
      return 2;
    }

    if (
      value.includes("10th") ||
      value.includes("secondary")
    ) {
      return 1;
    }

    return 0;
  };

  const sortedEducation =
    [...education].sort(
      (a, b) => {
        const rankA =
          educationRank(
            getCleanString(
              a.educationLevel
            )
          );

        const rankB =
          educationRank(
            getCleanString(
              b.educationLevel
            )
          );

        if (rankA !== rankB) {
          return rankB - rankA;
        }

        const endYearA =
          typeof a.endYear === "number"
            ? a.endYear
            : 0;

        const endYearB =
          typeof b.endYear === "number"
            ? b.endYear
            : 0;

        return endYearB - endYearA;
      }
    );

  const highestEducation =
    sortedEducation[0];

  if (!highestEducation) {
    return "";
  }

  return getFirstNonEmpty(
    highestEducation.degree,
    highestEducation.specialization,
    highestEducation.educationLevel
  );
};


const parseExperienceDuration = (
  duration: string
): number => {
  if (!duration) {
    return 0;
  }

  let months = 0;

  const yearMatch =
    duration.match(
      /(\d+(?:\.\d+)?)\s*(?:years?|yrs?)/i
    );

  if (yearMatch) {
    months += Math.round(
      Number(yearMatch[1]) * 12
    );
  }

  const monthMatch =
    duration.match(
      /(\d+(?:\.\d+)?)\s*(?:months?|mos?)/i
    );

  if (monthMatch) {
    months += Math.round(
      Number(monthMatch[1])
    );
  }

  return months;
};


const parseDateToMonth = (
  value?: string | null
): number | null => {
  if (!value) {
    return null;
  }

  const cleanValue =
    value.trim();

  if (!cleanValue) {
    return null;
  }

  /*
   * Supports:
   * YYYY
   * YYYY-MM
   * YYYY-MM-DD
   */
  const match =
    cleanValue.match(
      /^(\d{4})(?:-(\d{1,2}))?(?:-\d{1,2})?$/
    );

  if (!match) {
    return null;
  }

  const year =
    Number(match[1]);

  const month =
    match[2]
      ? Number(match[2])
      : 1;

  if (
    year < 1900 ||
    month < 1 ||
    month > 12
  ) {
    return null;
  }

  return (
    year * 12 +
    (month - 1)
  );
};


const deriveTotalExperienceMonths = (
  parsedData: ResumeParsedData
): number => {
  const experience =
    parsedData.experience || [];

  if (!experience.length) {
    return 0;
  }

  const intervals: Array<{
    start: number;
    end: number;
  }> = [];

  experience.forEach((item) => {
    const start =
      parseDateToMonth(
        item.startDate
      );

    const end =
      item.currentlyWorking
        ? (() => {
          const now =
            new Date();

          return (
            now.getFullYear() *
            12 +
            now.getMonth()
          );
        })()
        : parseDateToMonth(
          item.endDate
        );

    if (
      start !== null &&
      end !== null &&
      end >= start
    ) {
      intervals.push({
        start,
        end,
      });
    }
  });

  if (intervals.length) {
    intervals.sort(
      (a, b) =>
        a.start - b.start
    );

    const merged: Array<{
      start: number;
      end: number;
    }> = [];

    for (
      const interval of intervals
    ) {
      const last =
        merged[merged.length - 1];

      if (
        !last ||
        interval.start >
        last.end + 1
      ) {
        merged.push({
          ...interval,
        });

        continue;
      }

      last.end =
        Math.max(
          last.end,
          interval.end
        );
    }

    const totalMonths =
      merged.reduce(
        (
          total,
          interval
        ) =>
          total +
          (
            interval.end -
            interval.start +
            1
          ),
        0
      );

    if (totalMonths > 0) {
      return totalMonths;
    }
  }

  return experience.reduce(
    (
      total,
      item
    ) =>
      total +
      parseExperienceDuration(
        getCleanString(
          item.duration
        )
      ),
    0
  );
};


/*
 * Convert calculated resume experience
 * into one of the values supported by
 * the existing ProfileReviewStep Select.
 */
const mapExperienceToFormOption = (
  months: number
): string => {
  if (!months || months <= 0) {
    return "";
  }

  if (months < 12) {
    return "Less than 1 year";
  }

  if (months < 24) {
    return "1-2 years";
  }

  if (months < 36) {
    return "2-3 years";
  }

  if (months < 60) {
    return "3-5 years";
  }

  if (months < 96) {
    return "5-8 years";
  }

  if (months < 120) {
    return "8-10 years";
  }

  return "10+ years";
};


export function useCandidateProfileForm() {
  const [
    candidateProfile,
    setCandidateProfile,
  ] =
    useState<CandidateApplicationData>(
      initialCandidateProfile
    );


  const [
    profileError,
    setProfileError,
  ] =
    useState<string | null>(
      null
    );

  const [
    profileFieldErrors,
    setProfileFieldErrors,
  ] = useState<
    Partial<Record<ProfileField, string>>
  >({});


  const updateCandidateProfile = (
    field: keyof CandidateApplicationData,
    value: string
  ) => {
    setProfileError(null);

    if (
      [
        "fullName",
        "email",
        "phone",
        "currentLocation",
        "currentCompany",
        "currentDesignation",
        "totalExperience",
        "highestQualification",
        "currentSalary",
        "expectedSalary",
        "noticePeriod",
        "linkedinUrl",
      ].includes(field)
    ) {
      setProfileFieldErrors((previousErrors) => {
        const nextErrors = {
          ...previousErrors,
        };

        delete nextErrors[field as ProfileField];

        return nextErrors;
      });
    }
    setCandidateProfile(
      (previousProfile) => ({
        ...previousProfile,
        [field]: value,
      })
    );
  };


  const prefillCandidateProfile = (
    parsedData: ResumeParsedData | null,
    accountProfile?: {
      fullName?: string;
      email?: string;
    }
  ) => {
    if (!parsedData) {
      return;
    }

    const currentExperience =
      getCurrentOrLatestExperience(
        parsedData
      );

    const candidate =
      parsedData.candidate;


    const currentLocation =
      getFirstNonEmpty(
        candidate?.location,
        [
          candidate?.city,
          candidate?.state,
        ]
          .filter(Boolean)
          .join(", ")
      );


    const portfolioUrl =
      getFirstNonEmpty(
        candidate?.portfolio,
        candidate?.github,
        candidate?.website
      );


    const totalExperienceMonths =
      deriveTotalExperienceMonths(
        parsedData
      );


    const totalExperience =
      mapExperienceToFormOption(
        totalExperienceMonths
      );


    const highestQualification =
      getHighestQualification(
        parsedData
      );


    const skills =
      buildSkillsValue(
        parsedData
      );


    setProfileError(null);


    setCandidateProfile(
      (previousProfile) => {
        const nextProfile = {
          ...previousProfile,
        };


        /*
         * Only fill empty fields.
         */
        const setIfEmpty = (
          field: keyof CandidateApplicationData,
          value: string
        ) => {
          if (
            !getCleanString(
              nextProfile[field]
            ) &&
            getCleanString(value)
          ) {
            nextProfile[field] =
              value;
          }
        };


        /*
         * Candidate
         */
        if (getCleanString(accountProfile?.fullName)) {
          nextProfile.fullName = getCleanString(
            accountProfile?.fullName
          );
        }

        if (getCleanString(accountProfile?.email)) {
          nextProfile.email = getCleanString(
            accountProfile?.email
          );
        }


        setIfEmpty(
          "phone",
          normalizePhoneNumber(candidate?.phone)
        );


        setIfEmpty(
          "currentLocation",
          currentLocation
        );


        /*
         * URLs
         */
        setIfEmpty(
          "linkedinUrl",
          getCleanString(
            candidate?.linkedin
          )
        );


        setIfEmpty(
          "portfolioUrl",
          portfolioUrl
        );


        /*
         * Current / latest experience
         */
        setIfEmpty(
          "currentCompany",
          getCleanString(
            currentExperience?.company
          )
        );


        setIfEmpty(
          "currentDesignation",
          getCleanString(
            currentExperience?.designation
          )
        );


        /*
         * Experience
         */
        setIfEmpty(
          "totalExperience",
          totalExperience
        );


        /*
         * Education
         */
        setIfEmpty(
          "highestQualification",
          highestQualification
        );


        /*
         * Skills
         */
        setIfEmpty(
          "skills",
          skills
        );



        return nextProfile;
      }
    );
  };


  const validateCandidateProfile = () => {
    const errors: Partial<
      Record<ProfileField, string>
    > = {};

    if (!candidateProfile.fullName.trim()) {
      errors.fullName =
        "Please enter your full name.";
    }

    if (!candidateProfile.email.trim()) {
      errors.email =
        "Please enter your email address.";
    } else {
      const emailPattern =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (
        !emailPattern.test(
          candidateProfile.email.trim()
        )
      ) {
        errors.email =
          "Please enter a valid email address.";
      }
    }

    if (!candidateProfile.phone.trim()) {
      errors.phone =
        "Please enter your phone number.";
    } else {
      const phonePattern =
        /^[6-9]\d{9}$/;

      if (
        !phonePattern.test(
          candidateProfile.phone.trim()
        )
      ) {
        errors.phone =
          "Please enter a valid 10-digit mobile number.";
      }
    }

    if (!candidateProfile.currentLocation.trim()) {
      errors.currentLocation =
        "Please enter your current location.";
    }

    if (!candidateProfile.currentCompany.trim()) {
      errors.currentCompany =
        "Please enter your current company.";
    }

    if (!candidateProfile.currentDesignation.trim()) {
      errors.currentDesignation =
        "Please enter your current designation.";
    }

    if (!candidateProfile.totalExperience.trim()) {
      errors.totalExperience =
        "Please select your total experience.";
    }

    if (!candidateProfile.highestQualification.trim()) {
      errors.highestQualification =
        "Please enter your highest qualification.";
    }

    const linkedinUrl = candidateProfile.linkedinUrl.trim();

    if (linkedinUrl) {
      try {
        const url = new URL(linkedinUrl);

        if (
          !["http:", "https:"].includes(url.protocol) ||
          !["linkedin.com", "www.linkedin.com"].includes(
            url.hostname.toLowerCase()
          ) ||
          !url.pathname.startsWith("/in/")
        ) {
          errors.linkedinUrl =
            "Please enter a valid LinkedIn profile URL.";
        }
      } catch {
        errors.linkedinUrl =
          "Please enter a valid LinkedIn profile URL.";
      }
    }

    const portfolioUrl = candidateProfile.portfolioUrl.trim();

    if (portfolioUrl) {
      try {
        const url = new URL(portfolioUrl);

        if (!["http:", "https:"].includes(url.protocol)) {
          errors.portfolioUrl =
            "Please enter a valid Portfolio / GitHub URL.";
        }
      } catch {
        errors.portfolioUrl =
          "Please enter a valid Portfolio / GitHub URL.";
      }
    }

    setProfileFieldErrors(errors);

    return Object.values(errors)[0] ?? null;
  };


  const buildApplicationPayload = (
    draftId: string | null
  ) => ({
    draftId,

    fullName:
      candidateProfile.fullName,

    email:
      candidateProfile.email,

    phone:
      candidateProfile.phone,

    currentLocation:
      candidateProfile.currentLocation,

    totalExperienceMonths:
      candidateProfile.totalExperience,

    currentCompany:
      candidateProfile.currentCompany,

    currentDesignation:
      candidateProfile.currentDesignation,

    highestQualification:
      candidateProfile.highestQualification,

    currentSalary:
      candidateProfile.currentSalary,

    expectedSalary:
      candidateProfile.expectedSalary,

    noticePeriod:
      candidateProfile.noticePeriod,

    linkedinUrl:
      candidateProfile.linkedinUrl,

    portfolioUrl:
      candidateProfile.portfolioUrl.trim() || undefined,

    skills:
      candidateProfile.skills,
  });

  const reset = () => {
    setCandidateProfile({
      ...initialCandidateProfile,
    });

    setProfileError(null);
    setProfileFieldErrors({});
  };


  return {
    candidateProfile,
    profileError,
    profileFieldErrors,
    setProfileError,
    updateCandidateProfile,
    prefillCandidateProfile,
    validateCandidateProfile,
    buildApplicationPayload,
    reset,
  };
}